import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHead from "../SectionHead";
import { Section, CardNodes } from "./common";
import { container, item, inView } from "./presets";
import { ArrowUpRight, ChevronDown, Plus } from "lucide-react";

const T = (dir, ar, en) => (dir === "rtl" ? ar : en);
const num = (i) => String(i + 1).padStart(2, "0");

/* ---------- Variant: Grid / Bento ---------- */
function OffGrid({ heading, groups, dark, dir }) {
  const bento = groups.length >= 4;
  return (
    <Section dark={dark}>
      {heading && <SectionHead tag={T(dir, "خدماتنا", "What we offer")} title={heading} />}
      <motion.div {...inView} variants={container} className={`${heading ? "mt-12" : ""} grid sm:grid-cols-2 lg:grid-cols-3 gap-6`}>
        {groups.map((g, i) => (
          <motion.div
            key={i}
            variants={item}
            whileHover={{ y: -8 }}
            className={`rq-card p-7 h-full group relative overflow-hidden ${bento && i === 0 ? "lg:col-span-2" : ""}`}
          >
            <span className="absolute inset-x-0 top-0 h-[3px] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" style={{ background: "linear-gradient(90deg,#0b5e2e,#14a24b)" }} />
            <div className="flex items-center justify-between">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-[rgba(14,122,58,0.12)] text-[color:var(--rq-green)] font-display font-bold">{num(i)}</span>
              <ArrowUpRight size={20} className="text-[color:var(--rq-green)] opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300" />
            </div>
            {g.title && <h3 className="font-display mt-5 text-lg font-semibold text-white leading-snug">{g.title}</h3>}
            <CardNodes nodes={g.nodes} small />
          </motion.div>
        ))}
      </motion.div>
    </Section>
  );
}

/* ---------- Variant: Accordion ---------- */
function OffAccordion({ heading, groups, dark, dir }) {
  const [open, setOpen] = useState(0);
  return (
    <Section dark={dark}>
      {heading && <SectionHead tag={T(dir, "خدماتنا", "What we offer")} title={heading} />}
      <motion.div {...inView} variants={container} className={`${heading ? "mt-12" : ""} max-w-4xl mx-auto grid gap-3`}>
        {groups.map((g, i) => {
          const isOpen = open === i;
          return (
            <motion.div key={i} variants={item} className="rq-card overflow-hidden">
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center gap-4 p-5 md:p-6 text-start"
                data-testid={`offering-accordion-${i}`}
              >
                <span className={`grid place-items-center h-10 w-10 rounded-xl font-display font-bold transition-colors duration-300 ${isOpen ? "bg-[color:var(--rq-green)] text-white" : "bg-[rgba(14,122,58,0.12)] text-[color:var(--rq-green)]"}`}>{num(i)}</span>
                <h3 className="flex-1 font-display text-base md:text-lg font-semibold text-white leading-snug">{g.title}</h3>
                <ChevronDown size={20} className={`text-[color:var(--rq-green)] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
                    <div className="px-5 md:px-6 pb-6 ps-[76px]">
                      <CardNodes nodes={g.nodes} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </motion.div>
    </Section>
  );
}

/* ---------- Variant: Tabs ---------- */
function OffTabs({ heading, groups, dark, dir }) {
  const [active, setActive] = useState(0);
  const g = groups[active];
  return (
    <Section dark={dark}>
      {heading && <SectionHead tag={T(dir, "خدماتنا", "What we offer")} title={heading} />}
      <div className={`${heading ? "mt-12" : ""} grid lg:grid-cols-[300px_1fr] gap-6 lg:gap-10 items-start`}>
        <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0 lg:overflow-visible">
          {groups.map((s, i) => {
            const on = active === i;
            return (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`offering-tab-${i}`}
                className={`relative shrink-0 lg:shrink text-start rounded-xl px-4 py-3.5 text-sm font-semibold transition-all duration-300 flex items-center gap-3 ${on ? "text-white" : "text-[color:var(--rq-muted)] hover:text-white"}`}
                style={on ? { background: "rgba(14,122,58,0.14)", boxShadow: "inset 3px 0 0 var(--rq-green)" } : undefined}
              >
                <span className={`font-display ${on ? "text-[color:var(--rq-green)]" : "opacity-60"}`}>{num(i)}</span>
                <span className="line-clamp-2">{s.title}</span>
              </button>
            );
          })}
        </div>
        <div className="rq-card p-7 md:p-9 min-h-[260px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.35 }}>
              <span className="text-6xl font-display font-bold rq-gradient-text leading-none">{num(active)}</span>
              {g.title && <h3 className="font-display mt-4 text-xl md:text-2xl font-semibold text-white leading-snug">{g.title}</h3>}
              <CardNodes nodes={g.nodes} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Section>
  );
}

/* ---------- Variant: Zig-zag editorial rows ---------- */
function OffZigzag({ heading, groups, dark, dir }) {
  return (
    <Section dark={dark}>
      {heading && <SectionHead tag={T(dir, "خدماتنا", "What we offer")} title={heading} />}
      <motion.div {...inView} variants={container} className={`${heading ? "mt-14" : ""} grid gap-6`}>
        {groups.map((g, i) => {
          const rev = i % 2 === 1;
          return (
            <motion.div key={i} variants={item} className="rq-card p-7 md:p-9 grid md:grid-cols-[auto_1fr] gap-6 md:gap-9 items-center group">
              <div className={`font-display font-black leading-none rq-gradient-text text-6xl md:text-7xl ${rev ? "md:order-2" : ""}`}>{num(i)}</div>
              <div className={rev ? "md:order-1" : ""}>
                {g.title && <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-snug flex items-center gap-3"><Plus size={18} className="text-[color:var(--rq-green)] transition-transform duration-300 group-hover:rotate-90" />{g.title}</h3>}
                <CardNodes nodes={g.nodes} />
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </Section>
  );
}

export function Offerings({ variant, ...props }) {
  if (variant === "accordion") return <OffAccordion {...props} />;
  if (variant === "tabs") return <OffTabs {...props} />;
  if (variant === "zigzag") return <OffZigzag {...props} />;
  return <OffGrid {...props} />;
}
